import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import appConfig from "../../appConfig";
import { getToken } from "../../services/apiService";
import { describeIssues, issueLabel } from "./bomView";
import { COMPARISON_VIEWER } from "./flags";
import CodeStringBreakdown from "../bomScheduler/CodeStringBreakdown";

// Pieces shared by the BOM viewer and the comparison viewer pages

export const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : null;

// "Test 4 · ESOI ... · Format 301 · Oct 7, 2026"
export const runDetails = (input, source) =>
  [
    input.test && `Test ${input.test}`,
    input.esoi && `ESOI ${input.esoi}`,
    input.configurationId && input.configurationId !== input.esoi && (source === "Infor" ? `Config ${input.configurationId}` : `ESOI ${input.configurationId}`),
    input.workOrder && `WO ${input.workOrder}`,
    input.plant && `Plant ${input.plant}`,
    input.agileRevision && `Agile rev ${input.agileRevision}`,
    source === "Infor" && input.formatId && `Format ${input.formatId}`,
    formatDate(input.createdAt),
  ].filter(Boolean);

// URL keeps the tab, search and view, so a copied link opens the same thing.
// setParam(key, value), or setParam({ key: value, ... }) to change several at once; a falsy value removes the key
export function useViewerParams() {
  const [params, setParams] = useSearchParams();
  const setParam = useCallback(
    (key, value) =>
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const changes = typeof key === "object" ? key : { [key]: value };
          Object.entries(changes).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
          return next;
        },
        { replace: true }
      ),
    [setParams]
  );
  return [params, setParam];
}

const hasIssues = (c) => c && c.notFound + c.error > 0;

// Red count next to a tab name
export function IssueBadge({ counts }) {
  if (!hasIssues(counts)) return null;
  const total = counts.notFound + counts.error;
  return (
    <span className="bom-issue-badge" title={describeIssues(counts)}>
      {total.toLocaleString()} {issueLabel(counts)}
    </span>
  );
}

export function useDocumentTitle(title) {
  useEffect(() => {
    document.title = title;
    return () => {
      document.title = "DS Simulator Data Forge";
    };
  }, [title]);
}

export function LoadingCard({ text }) {
  return (
    <div className="bom-viewer text-start">
      <div className="card border-0 shadow-sm">
        <div className="card-body p-4 text-muted" role="status">{text}</div>
      </div>
    </div>
  );
}

export function ErrorCard({ text }) {
  return (
    <div className="bom-viewer text-start">
      <div className="card border-0 shadow-sm">
        <div className="card-body p-4">
          <Link to="/" className="small">&larr; Back to BOM Compare</Link>
          <div className="alert alert-danger mt-3 mb-0">{text}</div>
        </div>
      </div>
    </div>
  );
}

// Downloads a saved file; status is null, "Downloading..." or "Download failed"
export function useDownload(instance, accounts) {
  const [status, setStatus] = useState(null);
  const download = async (outputId, fileName) => {
    setStatus("Downloading...");
    try {
      const token = await getToken(instance, accounts);
      const response = await fetch(`${appConfig.apiUrl}outputs/download/${outputId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(String(response.status));
      const url = window.URL.createObjectURL(await response.blob());
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setStatus(null);
    } catch {
      setStatus("Download failed");
    }
  };
  return [status, download];
}

export function CodeStringLine({ input }) {
  const [show, setShow] = useState(false);
  if (!input.codeString) return null;
  const canBreakDown = /^\d+$/.test(String(input.formatId || ""));
  return (
    <div className="bom-viewer__code-line">
      <div className="bom-viewer__code" title={input.codeString}>{input.codeString}</div>
      {canBreakDown && (
        <>
          <button type="button" className="btn btn-sm btn-link p-0 bom-viewer__breakdown" onClick={() => setShow(true)}>
            Break down
          </button>
          <CodeStringBreakdown
            show={show}
            readOnly
            formatNumber={String(input.formatId)}
            codeString={input.codeString.trim()}
            onHide={() => setShow(false)}
            onApply={() => setShow(false)}
          />
        </>
      )}
    </div>
  );
}

export function CopyLinkButton() {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
    } catch {
      window.prompt("Copy this link", window.location.href);
    }
  };
  return (
    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={copy} title="Copy the link to this view to share it">
      <span aria-live="polite">{copied ? "Link copied" : "Copy link"}</span>
    </button>
  );
}

export function DownloadStatus({ status }) {
  if (!status) return null;
  return (
    <span className={`small ${status === "Downloading..." ? "text-muted" : "text-danger"}`} role="status">
      {status}
    </span>
  );
}

// The run's BOMs and its comparison, [Dev] [Prd] [Dev vs Prd]. The tab, search and NOT FOUND filter carry over.
export function ViewSwitch({ links, current }) {
  const [params] = useSearchParams();
  if (!COMPARISON_VIEWER || !links || links.length < 2) return null;
  const keep = new URLSearchParams();
  ["tab", "q", "issues"].forEach((key) => params.get(key) && keep.set(key, params.get(key)));
  const search = keep.toString() ? `?${keep}` : "";
  return (
    <nav className="btn-group btn-group-sm bom-switch" aria-label="BOMs of this run">
      {links.map((link) => {
        const active = link.kind === current.kind && link.id === current.id;
        return (
          <Link
            key={`${link.kind}-${link.id}`}
            to={`/${link.kind}/${link.id}${search}`}
            className={`btn btn-outline-primary${active ? " active" : ""}`}
            aria-current={active ? "page" : undefined}
          >
            {link.label}
          </Link>
        );
      })}
    </nav>
  );
}

// Tabs of the file; badge(section) is the number next to the name
export function SectionTabs({ sections, current, onSelect, badge }) {
  if (sections.length < 2) return null;
  return (
    <ul className="nav nav-tabs bom-viewer__tabs" role="tablist">
      {sections.map((s) => (
        <li className="nav-item" key={s.name} role="presentation">
          <button
            type="button"
            role="tab"
            aria-selected={s === current}
            className={`nav-link${s === current ? " active" : ""}${s.rows.length === 0 ? " is-empty" : ""}`}
            onClick={() => onSelect(s)}
          >
            {s.name} {badge(s)}
          </button>
        </li>
      ))}
    </ul>
  );
}
